// frontend/src/pages/public/ServiceReviewsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Star, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const UPLOADS_BASE = process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || 'http://localhost:5000';

const serviceLabels: Record<string, string> = {
  'svincolo-atti': 'Svincolo Atti',
  'voucher-rimborso': 'Voucher Rimborso',
  'collabora': 'Collabora'
};

const serviceColors: Record<string, string> = {
  'svincolo-atti': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  'voucher-rimborso': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  'collabora': 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300'
};

interface Review {
  _id: string;
  associateSnapshot: {
    nominativo: string;
    ragioneSociale: string;
    citta: string;
    provincia: string;
    foto: string;
  };
  serviceType: string;
  rating: number;
  comment: string;
  tags: string[];
  publishedAt: string;
}

interface ServiceStat {
  _id: string;
  avgRating: number;
  count: number;
}

const ServiceReviewsPage: React.FC = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [serviceStats, setServiceStats] = useState<ServiceStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filterService, setFilterService] = useState('');
  const [filterRating, setFilterRating] = useState('');
  const [sort, setSort] = useState('recent');

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (filterService) params.set('serviceType', filterService);
      if (filterRating) params.set('rating', filterRating);
      if (sort === 'rating') params.set('sort', 'rating');

      const { data } = await axios.get(`${API_BASE}/public/service-reviews?${params}`);
      setReviews(data.reviews);
      setTotalPages(data.totalPages);
      setTotal(data.total);
      setServiceStats(data.serviceStats || []);
    } catch {
      // silenzioso per pagina pubblica
    } finally {
      setLoading(false);
    }
  }, [page, filterService, filterRating, sort]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const globalAvg = serviceStats.length > 0
    ? serviceStats.reduce((sum, s) => sum + s.avgRating * s.count, 0) / serviceStats.reduce((sum, s) => sum + s.count, 0)
    : 0;

  const renderStars = (rating: number) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(s => (
        <Star
          key={s}
          className={`w-4 h-4 ${s <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}`}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-base font-bold mb-2">Recensioni dei nostri associati</h1>
          <p className="text-sm text-blue-200 mb-6">
            Scopri cosa dicono i membri della Rete Certificata UNASCA sui servizi
          </p>
          {total > 0 && (
            <div className="flex items-center justify-center gap-3">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-5 h-5 ${s <= Math.round(globalAvg) ? 'text-yellow-400 fill-yellow-400' : 'text-blue-400'}`} />
                ))}
              </div>
              <span className="text-base font-bold">{globalAvg.toFixed(1)}</span>
              <span className="text-sm text-blue-200">su 5 — {total} recensioni</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Stats per servizio */}
        {serviceStats.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {serviceStats.map(stat => (
              <div key={stat._id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">{serviceLabels[stat._id] || stat._id}</p>
                <div className="flex items-center justify-center gap-2 mt-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="text-sm font-bold text-gray-900 dark:text-white">{stat.avgRating.toFixed(1)}</span>
                  <span className="text-2xs text-gray-400">({stat.count})</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <Filter className="w-4 h-4 text-gray-400" />
          <select
            value={filterService}
            onChange={(e) => { setFilterService(e.target.value); setPage(1); }}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
          >
            <option value="">Tutti i servizi</option>
            <option value="svincolo-atti">Svincolo Atti</option>
            <option value="voucher-rimborso">Voucher Rimborso</option>
            <option value="collabora">Collabora</option>
          </select>
          <select
            value={filterRating}
            onChange={(e) => { setFilterRating(e.target.value); setPage(1); }}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
          >
            <option value="">Tutte le valutazioni</option>
            <option value="5">5 stelle</option>
            <option value="4">4 stelle</option>
            <option value="3">3 stelle</option>
            <option value="2">2 stelle</option>
            <option value="1">1 stella</option>
          </select>
          <select
            value={sort}
            onChange={(e) => { setSort(e.target.value); setPage(1); }}
            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm text-gray-900 dark:text-white"
          >
            <option value="recent">Più recenti</option>
            <option value="rating">Più votate</option>
          </select>
        </div>

        {/* Reviews list */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Caricamento...</div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Nessuna recensione disponibile
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reviews.map(review => (
              <div key={review._id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
                <div className="flex items-start gap-3 mb-3">
                  {review.associateSnapshot?.foto ? (
                    <img
                      src={`${UPLOADS_BASE}${review.associateSnapshot.foto}`}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-sm font-medium text-blue-600 dark:text-blue-300 flex-shrink-0">
                      {(review.associateSnapshot?.nominativo || '?')[0]}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {review.associateSnapshot?.ragioneSociale || review.associateSnapshot?.nominativo}
                    </p>
                    <p className="text-2xs text-gray-500 dark:text-gray-400">
                      {review.associateSnapshot?.citta}{review.associateSnapshot?.provincia ? ` (${review.associateSnapshot.provincia})` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  {renderStars(review.rating)}
                  <span className={`px-2 py-0.5 rounded-full text-2xs ${serviceColors[review.serviceType]}`}>
                    {serviceLabels[review.serviceType]}
                  </span>
                </div>
                {review.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {review.tags.map(tag => (
                      <span key={tag} className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full text-2xs capitalize">{tag}</span>
                    ))}
                  </div>
                )}
                {review.comment && (
                  <p className="text-sm text-gray-700 dark:text-gray-300">{review.comment}</p>
                )}
                <p className="text-2xs text-gray-400 mt-3">
                  {new Date(review.publishedAt).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Pagina {page} di {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceReviewsPage;
